import { useCallback, useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";

import { Auth } from "aws-amplify";
import { genericContactUsMessage } from "atoms/auth";
import { useNavigate } from "react-router-dom";
import { useSetRecoilState } from "recoil";
import { getUserStatus, resendEmail } from "api/services";

export const usePassword = () => {
  const [email, setEmail] = useState("");

  const { data, status, isFetching } = useQuery(
    ["getUserStatus", { email }],
    () => getUserStatus(email),
    {
      enabled: Boolean(email)
    }
  );

  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const { mutateAsync } = useMutation(resendEmail);
  const [errorMessage, setErrorMessage] = useState(undefined);

  const setContactUsMessage = useSetRecoilState(genericContactUsMessage);

  const navigateToProperPasswordFlow = useCallback(async () => {
    if (data === undefined) return;

    if (data.userStatus === "FORCE_CHANGE_PASSWORD") {
      try {
        setIsLoading(true);
        await mutateAsync({ email });

        setContactUsMessage("login.cognito.temporaryPassword.supportEmail");

        navigate("/password/temporary", { state: { email } });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (err: any) {
        setIsLoading(false);

        setErrorMessage(err.message);
      }
    } else if (
      data.userStatus === "CONFIRMED" ||
      data.userStatus === "RESET_REQUIRED" ||
      "unknown"
    ) {
      try {
        setIsLoading(true);
        await Auth.forgotPassword(email);

        setContactUsMessage("login.cognito.resetPassword.supportEmail");

        navigate("/password/reset", { state: { email } });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (err: any) {
        setIsLoading(false);
        setErrorMessage(err?.message);
      }
    }
  }, [data, email, navigate, mutateAsync, setContactUsMessage]);

  useEffect(() => {
    if (data !== undefined) {
      navigateToProperPasswordFlow();
    }
  }, [data, navigateToProperPasswordFlow]);

  return {
    setEmail,
    errorMessage,
    isNetworkError: status === "error",
    isLoading: isLoading || isFetching
  };
};
